import { useState } from "react";
import { useGetBIN } from "@/hooks/use-queries";
import { binCards } from "@/pages/dashboard/shop/page";

type PlatinumFeeSource = {
  provider: string;
  isActive?: boolean;
  price: number | string;
  defaultPrice?: number | string;
  topUpFee: number | string;
  minDepositAmount: number;
  maxDepositAmount: number;
};

const tabs = ["Sapphire Card", "Platinum Card"] as const;

const formatRange = (
  values: number[],
  { prefix = "", suffix = "" }: { prefix?: string; suffix?: string } = {},
) => {
  const nums = values.filter((n) => Number.isFinite(n));
  if (!nums.length) return "—";
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  if (min === max) return `${prefix}${min.toLocaleString()}${suffix}`;
  return `${prefix}${min.toLocaleString()} - ${prefix}${max.toLocaleString()}${suffix}`;
};

const TermsAndConditions = () => {
  const [activeTab, setActiveTab] =
    useState<(typeof tabs)[number]>("Sapphire Card");
  const { data: binsData } = useGetBIN();

  const bins = (() => {
    const raw = binsData as any;
    return Array.isArray(raw) ? raw : (raw?.data ?? []);
  })();

  const sapphireBin = bins.find((b: any) => b.provider === "int");
  const sapphireCardPurchaseFee = sapphireBin?.price ?? binCards.sapphire.price;
  const sapphireCardFundingFee = sapphireBin?.topUpFee ?? 0;

  // Combine Visa + MasterCard platinum bins so fees can be shown as a
  // smallest-to-biggest range covering both networks.
  const platinumBins: PlatinumFeeSource[] = (() => {
    const active = bins.filter((b: any) => b.provider === "wsb" && b.isActive);
    return active.length ? active : [binCards.platinum, binCards.platinumBasic];
  })();

  const cardPurchaseFeeDefaultRange = formatRange(
    platinumBins.map((b) => Number(b.defaultPrice ?? b.price)),
    { suffix: " USD" },
  );
  const cardPurchaseFeeCustomRange = formatRange(
    platinumBins.map((b) => Number(b.price)),
    { suffix: " USD" },
  );
  const platinumTopUpFees = platinumBins.map((b) => Number(b.topUpFee));
  const platinumCardFundingFee = platinumTopUpFees.every((n) => n === 0)
    ? "FREE"
    : formatRange(platinumTopUpFees, { suffix: "%" });
  const minDepositRange = formatRange(
    platinumBins.map((b) => Number(b.minDepositAmount)),
    { prefix: "$" },
  );
  const maxDepositRange = formatRange(
    platinumBins.map((b) => Number(b.maxDepositAmount)),
    { prefix: "$", suffix: " USD" },
  );

  return (
    <div>
      <div className="text-dark-text-400">
        <h3 className="font-semibold">Terms & Conditions</h3>

        <div
          className="mt-4 flex h-[35px] w-full max-w-[280px] items-center gap-2 rounded-full border border-[#D9D9D9] p-0.5"
          style={{
            background:
              "linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(153, 153, 153, 0.1) 100%)",
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              className={`h-full w-full rounded-full text-xs transition-colors ${
                activeTab === tab
                  ? "bg-[#B37AF2] text-white"
                  : "text-dark-text-300"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === "Sapphire Card" ? (
          <>
            <h4 className="mt-6 text-xs font-semibold lg:mt-4">
              Supported Usage Scenarios
            </h4>
            <p className="mt-2 text-xs leading-5 font-light">
              The card DOES NOT currently support Uber, Petrol stations, Talabat,
              Hermes Amsterdam, Hermes Paris, and Iberia and a few other
              merchants.
            </p>
            <h4 className="mt-4 text-xs font-semibold">Card information </h4>
            <ol className="list-decimal pl-8 text-xs leading-4 *:font-light">
              <li>Card Issuer: Visa</li>
              <li>Card Type: Credit</li>
              <li>Card Form: Virtual</li>
              <li>Currency: USD</li>
              <li>Apple Pay supported: Yes</li>
              <li>Google Wallet supported: Yes</li>
            </ol>
            <h4 className="mt-4 text-xs font-semibold">Fees</h4>
            <p className="text-xs leading-5 font-light">
              Card Purchase Fee: {sapphireCardPurchaseFee} USD
            </p>{" "}
            <p className="text-xs leading-5 font-light">
              Card Funding Fee:{" "}
              {Number(sapphireCardFundingFee) === 0
                ? "FREE"
                : `${sapphireCardFundingFee}%`}
            </p>
            <p className="text-xs leading-5 font-light">
              FX Fee: This can vary and only applies if a crossborder
              transaction or the billing currency is different from the
              settlement currency.
            </p>
            <h4 className="mt-4 text-xs font-semibold">Credit Limits</h4>
            <ol className="list-decimal pl-8 text-xs leading-4 font-light">
              <li>Maximum Deposit: $100,000 USD</li>
              <li>Maximum Card Purchase Quantity: 10</li>
              <li>Initial Deposit Required: Yes</li>
            </ol>
            <h4 className="mt-4 text-xs font-semibold">Card Usage Notice</h4>
            <ol className="list-decimal pl-8 text-xs leading-4 font-light">
              <li>
                If the card issuer detects malicious activities, such as bulk
                refunds, cancellations, failures or chargebacks during card
                usage, the card will be automatically frozen, and a fee of $50
                USD per occurrence will be deducted.
              </li>
              <li>
                If the overall transaction failure rate exceeds 20%, the card
                will be automatically frozen.
              </li>
              <li>
                If the card has insufficient balance and accumulates up to 3
                consecutive authorization failures, the card will be
                automatically cancelled.
              </li>
              <li>
                If the card is frozen, please contact customer service to apply
                for unfreezing.
              </li>
              <li>The card is valid for 1 years.</li>
            </ol>
            <p className="mt-4 text-xs leading-5 font-light">
              <span className="font-medium">Card Currency:</span> Card currency
              refers to the currency type or unit associated with infinity
              cards. it plays a crucial role in determining the currency used
              for payment and settlement when utilising Vaulta cards. It is
              highly recommended to utilise cards that align with the currency
              of the purchase order to mitigate the potential incurring of FX
              fees.
            </p>
            <p className="mt-2 text-xs leading-5 font-light">
              For customers with monthly transaction volumes exceeding 500,000
              USD, please contact the support team for fee adjustments.
            </p>
          </>
        ) : (
          <>
            <h4 className="mt-6 text-xs font-semibold lg:mt-4">
              Supported Usage Scenarios
            </h4>
            <p className="mt-2 text-xs leading-5 font-light">
              The card SUPPORTS Uber (not available on the Platinum Visa card),
              petrol stations, YouTube, AliExpress, Amazon, Iberia, Talabat.
            </p>
            <h4 className="mt-4 text-xs font-semibold">Card information</h4>
            <ol className="list-decimal pl-8 text-xs leading-4 *:font-light">
              <li>Card Issuer: Visa / MasterCard</li>
              <li>Card Type: Prepaid</li>
              <li>Card Form: Virtual</li>
              <li>Currency: USD</li>
              <li>Apple Pay supported: Yes</li>
              <li>Google Wallet supported: Yes</li>
            </ol>
            <h4 className="mt-4 text-xs font-semibold">Fees</h4>
            <p className="text-xs leading-5 font-light">
              Card Purchase Fee (Default): {cardPurchaseFeeDefaultRange}
            </p>
            <p className="text-xs leading-5 font-light">
              Card Purchase Fee (Custom): {cardPurchaseFeeCustomRange}
            </p>
            <p className="text-xs leading-5 font-light">
              Card Funding Fee: {platinumCardFundingFee}
            </p>
            <p className="text-xs leading-5 font-light">
              FX Fee: This can vary and only applies if a crossborder
              transaction or the billing currency is different from the
              settlement currency.
            </p>
            <h4 className="mt-4 text-xs font-semibold">Credit Limits</h4>
            <ol className="list-decimal pl-8 text-xs leading-4 font-light">
              <li>Maximum Deposit: {maxDepositRange}</li>
              <li>Maximum Card Purchase Quantity: 10</li>
              <li>Initial Deposit Required: Yes (minimum {minDepositRange})</li>
            </ol>
            <h4 className="mt-4 text-xs font-semibold">Card Usage Notice</h4>
            <ol className="list-decimal pl-8 text-xs leading-4 font-light">
              <li>
                If the card issuer detects malicious activities, such as bulk
                refunds, cancellations, failures or chargebacks during card
                usage, the card will be automatically frozen, and a fee of $50
                USD per occurrence will be deducted.
              </li>
              <li>
                If the overall transaction failure rate exceeds 20%, the card
                will be automatically frozen.
              </li>
              <li>
                If the card has insufficient balance and accumulates up to 3
                consecutive authorization failures, the card will be
                automatically cancelled.
              </li>
              <li>
                If the card is frozen, please contact customer service to apply
                for unfreezing.
              </li>
              <li>The card is valid for 1 year.</li>
            </ol>
            <p className="mt-4 text-xs leading-5 font-light">
              <span className="font-medium">Card Currency:</span> It is highly
              recommended to utilise cards that align with the currency of the
              purchase order to mitigate the potential incurring of FX fees.
            </p>
            <p className="mt-2 text-xs leading-5 font-light">
              For customers with monthly transaction volumes exceeding 500,000
              USD, please contact the support team for fee adjustments.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default TermsAndConditions;
